import { Outlet, createRootRouteWithContext } from "@tanstack/react-router";
import type { QueryClient } from "@tanstack/react-query";
import { Suspense, lazy } from "react";

// Router devtools ship in dev only, and must be TREE-SHAKEN OUT of the prod bundle — not just
// hidden. That requires a COMPILE-TIME-foldable guard: Vite replaces the literal text
// `import.meta.env.PROD` with `true` in a prod build, folding this ternary to `() => null` so
// the `import("@tanstack/router-devtools")` is unreachable and Rollup drops the whole chunk.
//
// This is the ONE deliberate exception to "@core/config is the only reader of import.meta.env":
// `config.isProd` is a property of a runtime-validated object (its apiBaseUrl comes from a Zod
// parse), so the bundler cannot fold it — guarding on it leaves the devtools chunk in dist
// (verified). Runtime env consumers (logger, http-logging) correctly use config.isDev; only
// this build-time strip needs the raw literal. Verify by grepping dist for "router-devtools".
const RouterDevtools = import.meta.env.PROD
  ? () => null
  : lazy(() =>
      import("@tanstack/router-devtools").then((mod) => ({
        default: mod.TanStackRouterDevtools,
      }))
    );

/**
 * Router context carries the QueryClient so route `beforeLoad` guards can read the ambient
 * profile (`@core/auth` ensure/tryGetProfile) without importing the app singleton — that keeps
 * guard logic testable and the router the sole owner of the auth boundary.
 */
export interface RouterContext {
  queryClient: QueryClient;
}

export const Route = createRootRouteWithContext<RouterContext>()({
  component: RootLayout,
});

function RootLayout() {
  return (
    <>
      <Outlet />
      <Suspense>
        <RouterDevtools />
      </Suspense>
    </>
  );
}
